import { html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BaseElement } from '../base/base-element.js';

export type ModalSize = 'small' | 'medium' | 'wide';

/**
 * A modal dialog component with backdrop, keyboard handling, and size variants.
 *
 * @slot - Default slot for modal body content
 * @slot footer - Footer slot for action buttons
 *
 * @fires modal-open - Fired when the modal opens
 * @fires modal-close - Fired when the modal closes (detail: { reason: string })
 */
@customElement('modal-dialog')
export class ModalDialog extends BaseElement {
    static styles = [
        BaseElement.styles,
        css`
            :host {
                display: none;
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 2000;
            }

            :host([open]) {
                display: flex;
                align-items: center;
                justify-content: center;
            }

            .backdrop {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.7);
            }

            .dialog {
                position: relative;
                background: var(--surface, #1e293b);
                border-radius: 12px;
                padding: 2rem;
                width: 100%;
                max-height: 90vh;
                overflow-y: auto;
                z-index: 1;
            }

            /* Size variants */
            :host([size="small"]) .dialog {
                max-width: 400px;
            }

            :host([size="medium"]) .dialog {
                max-width: 600px;
            }

            :host([size="wide"]) .dialog {
                max-width: 900px;
            }

            .header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 1.5rem;
            }

            .heading {
                margin: 0;
                font-size: 1.25rem;
                font-weight: 600;
                color: var(--text-primary, #f1f5f9);
            }

            .close-button {
                font-size: 1.5rem;
                color: var(--text-secondary, #cbd5e1);
                padding: 0.25rem;
                line-height: 1;
                transition: color 0.2s;
            }

            .close-button:hover {
                color: var(--text-primary, #f1f5f9);
            }

            .body {
                color: var(--text-primary, #f1f5f9);
            }

            .footer {
                margin-top: 1.5rem;
            }

            ::slotted([slot="footer"]) {
                display: flex;
                gap: 0.75rem;
                justify-content: flex-end;
            }
        `,
    ];

    @property({ type: Boolean, reflect: true })
    open = false;

    @property({ type: String })
    heading = '';

    @property({ type: String, reflect: true })
    size: ModalSize = 'medium';

    @property({ type: Boolean, attribute: 'close-on-backdrop' })
    closeOnBackdrop = true;

    @property({ type: Boolean, attribute: 'close-on-escape' })
    closeOnEscape = true;

    @property({ type: Boolean, attribute: 'show-close-button' })
    showCloseButton = true;

    private _boundKeyDown = this._onKeyDown.bind(this);

    connectedCallback(): void {
        super.connectedCallback();
        document.addEventListener('keydown', this._boundKeyDown);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener('keydown', this._boundKeyDown);
    }

    private _onKeyDown(e: KeyboardEvent): void {
        if (e.key === 'Escape' && this.open && this.closeOnEscape) {
            this.hide('escape');
        }
    }

    private _onBackdropClick(): void {
        if (this.closeOnBackdrop) {
            this.hide('backdrop');
        }
    }

    show(): void {
        this.open = true;
        this.dispatchEvent(new CustomEvent('modal-open', { bubbles: true, composed: true }));
    }

    hide(reason = 'programmatic'): void {
        this.open = false;
        this.dispatchEvent(
            new CustomEvent('modal-close', {
                bubbles: true,
                composed: true,
                detail: { reason },
            })
        );
    }

    render() {
        return html`
            <div class="backdrop" @click=${this._onBackdropClick}></div>
            <div
                class="dialog"
                role="dialog"
                aria-modal="true"
                aria-label=${this.heading || 'Dialog'}
            >
                ${this.heading || this.showCloseButton
                    ? html`
                          <div class="header">
                              <h2 class="heading">${this.heading}</h2>
                              ${this.showCloseButton
                                  ? html`
                                        <button
                                            class="close-button"
                                            @click=${() => this.hide('close-button')}
                                            aria-label="Close"
                                        >
                                            &times;
                                        </button>
                                    `
                                  : ''}
                          </div>
                      `
                    : ''}
                <div class="body">
                    <slot></slot>
                </div>
                <div class="footer">
                    <slot name="footer"></slot>
                </div>
            </div>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'modal-dialog': ModalDialog;
    }
}
